'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import Box from '@mui/material/Box';
import CardContext from '@/contexts/CardContext';
import { textColors } from '@/styles/theme/colors';
import Chip from '@mui/material/Chip';
import DataContext from '@/contexts/DataContext';
import { DataTable } from '@/components/core/data-table';
import { dayjs } from '@/lib/dayjs';
import { fetchExprData, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { icons } from '@/components/dashboard/layout/nav-icons';
import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { logger } from '@/lib/default-logger';
import { useComponentLogger } from '@/lib/component-logger';
import { useInterval } from '@/hooks/use-interval';


// The first column
// Has an optional icon and the display name
const primaryFormatter = (row, index, column) => {
  if (!row[column.field]) return null;
    let cellIcon = row[column.field].icon;
    const RowIcon = cellIcon && icons[cellIcon] ? icons[cellIcon] : null;
    const iconWeight = cellIcon === 'thermometer-simple' ? 'fill' : 'bold';
    const rowVal = row[column.field];
    let dis = ''; 
    if (rowVal.dis) {
      dis = rowVal.unit !== null && rowVal.unit !== undefined ? rowVal.dis + rowVal.unit : rowVal.dis;
    } else if (rowVal.value) {
      dis = rowVal.unit !== null && rowVal.unit !== undefined ? rowVal.value + rowVal.unit : rowVal.value;
    } 
    // logger.debug('primary row', row, column, cellIcon, rowVal, dis);

    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: {xs: 'center', sm: 'flex-start'}}}>
        {/* // <Stack direction="row" spacing={1} sx={{ }}> */}
            {cellIcon ? 
                <Box>
                    <RowIcon size={24} weight={iconWeight}/>
                </Box>
            : <></>} 
            <Typography sx={{ typography: { xs: 'subtitle1', sm: 'subtitle1' } }}>
                {dis}
            </Typography>
        </Stack>
    );
}

// Displays the value as a colored chip
// if no color specified, shows plain text
const statusFormatter = (row, index, column) => {
    
    if (!row[column.field]) return null;
    const rowVal = row[column.field];
    const dis = rowVal.unit ? rowVal.value + rowVal.unit : rowVal.value;
    // logger.debug('status row:', row, index, column, rowVal );

    const mapping = {
      pending: { label: 'yellow', color: 'warning' },
      completed: { label: 'green', color: 'success' },
      canceled: { label: 'red', color: 'error' },
    };
    const { label, color } = mapping[row.status] ?? { label: 'Unknown', color: 'secondary' };

    // return <Chip style={{backgroundColor: 'var(--mui-palette-error-100)', color: 'white'}} label={row[column.field]} size="small" variant="soft" />;
    if (row[column.field].color) {
      return <Chip color={row[column.field].color} label={row[column.field].value} size="small" variant="soft" />;
    } else {
      return (
      <Typography sx={{ typography: 'body2' }}>
        {dis}
      </Typography>)
    }
} 

// Default formatter
// Renders and optional icon
// If a color is specified, applies color to text and icon
const iconFormatter = (row, index, column) => {
  if (!row[column.field]) return null;
  const rowVal = row[column.field];
  const dis = rowVal.unit ? rowVal.value + rowVal.unit : rowVal.value;
  // logger.debug('icon row', row, column, rowVal);

  let cellIcon = row[column.field].icon;
  const RowIcon = cellIcon && icons[cellIcon] ? icons[cellIcon] : null;
  let cellColor = row[column.field].color;
  const textColor = cellColor && textColors[cellColor] ? textColors[cellColor] : textColors['black'];

  return (
      // <Stack direction="row" spacing={1} sx={{ alignItems: 'center'}}>
      <Stack direction="row" spacing={1} sx={{ }}>
          {cellIcon ? 
              <Box sx={{marginRight: -0.5}}>
                  <RowIcon size={20} weight='fill' color={textColor}/>
              </Box>
          : <></>} 
          <Typography sx={{ typography:'body2', }} color={textColor}>
            {dis}
          </Typography>
      </Stack>
  );
}

export function CurValTable({config={}, shouldOverrideTitle, selectable=false, onDeselectAll, onDeselectOne, onSelectAll, onSelectOne, selected=[]}) {
  const logger = useComponentLogger("Table");
  const { currentProject, currentView, selectedViewParams, changedParams } = useContext(DataContext);
  const { cardTitle, setCardTitle } = useContext(CardContext);
  const [tableRows, setTableRows] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [title, setTitle] = useState(config?.title || '');
  const trackedParamValsRef = useRef({});
  const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr
  const [refreshInterval, setRefreshInterval] = useState(0);

  logger.debug('table object', config, selectable);

    // fetch and set content
    const fetchContent = async () => {
      logger.debug('fetch called', selectedViewParams);
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;
        
        if (dataSource.type === "text") {
            parseTableData(config.dataSource.text);
            // logger.debug('table content', tableObject.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('table data', data, dataSource.expr);
            parseTableData(data);
        }

    }

    const parseTableData = (data) => {
      // set card title
      if (data.meta && data.meta.title && shouldOverrideTitle) setCardTitle(data.meta.title);

      // rows is an array of 1 obj, with a key per data point
      // cols is an array of objects, 1 object epr data point
      if (!data?.rows || data.rows.length === 0 || !data?.cols || data.cols.length === 0) return;
      let displayMeta = data.meta?.presentation?.rows || [];

      // prune display meta
      let rowDisplay = displayMeta?.filter((display) => {
        if (!display.hasOwnProperty('col')) return false; // we currently only take col specs, modify this in the future if that changes
        if (display.row && display.row < 0) return false; // safeguard against -ve rows
        return true;
      });
      
      let formattedCols = [];
      let formattedRows = [];

      // build rows
      let iconColumns = [];
      let statusColumns = [];

    // logger.debug('row display', rowDisplay);
    // logger.debug('table rows', data.rows);

      for (const index in data.rows) {
        // logger.debug('cur row', index, data.rows[index], data.meta);
        let row = data.rows[index];
        for (const display of rowDisplay) {
          // Add to arrays to determine formatter function
          if (display?.mobileIcon) {
            if (!iconColumns.includes(display.col)) iconColumns.push(display.col);
          } else if (display?.background) {
            if (!statusColumns.includes(display.col)) statusColumns.push(display.col);
          }
        }
        

        // cells can either be an icon or status but not both
        // if a cell has both an icon and background, use the icon formatter
        statusColumns = statusColumns.filter(item => !iconColumns.includes(item));
        
        let rowConfig = {};

        for (const key in row) {
            let item = row[key];
            // logger.debug('cur row item', item);
            if (typeof(item) === 'object' && item !== null) {
                if (item.val === null || item.val === undefined) continue;
                rowConfig[key] = {
                  value: item.val,
                  unit: item.unit || null,
                  dis: item.dis || null
                };
            } else { // string
                rowConfig[key] = {value: item};
            }

        }

        
        formattedRows.push(rowConfig);
      }

    // logger.debug('formatted rows', formattedRows);
    // logger.debug('icon, status cols', iconColumns, statusColumns);

      for (const display of rowDisplay) {
      // logger.debug('cur display', display, formattedRows[display.row][display.col]);
        // Add to arrays to determine formatter function
        if (display?.mobileIcon) {
          if (!iconColumns.includes(display.col)) iconColumns.push(display.col);
        } else if (display?.background) {
          if (!statusColumns.includes(display.col)) statusColumns.push(display.col);
        }
        
        // in case a cell is empty?
        if (formattedRows[display.row][display.col]) {
          if (display?.background) formattedRows[display.row][display.col]['color'] = display.background;
          if (display?.mobileIcon) formattedRows[display.row][display.col]['icon'] = display.mobileIcon
        }
      }

      

      
      setTableRows(formattedRows);

      // build columns
      for (const col of data.cols) {
        // logger.debug('cur col', col, statusColumns);
        if (!col.name) continue;
        let colConfig = { field: col.name, name: col.meta?.dis || col.meta?.navName || col.name || ''};
        if (statusColumns.includes(col.name)) {
          colConfig.formatter = statusFormatter;
        } else {
          colConfig.formatter = iconFormatter;
        }
        formattedCols.push(colConfig);     
      }

      // diff styling for the first column
      if (formattedCols.length > 0) formattedCols[0].formatter = primaryFormatter;
      setTableColumns(formattedCols);

      // logger.debug('parsed rows', formattedRows, formattedCols, iconColumns, statusColumns);
    }
    
   
    useEffect(() => {
      logger.debug('table config', config)
      let trackedParams = extractTrackedParams(config.dataSource);
      let params = {};
      for (const param of trackedParams) {
          if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
      }

      logger.debug('tracked params', params);
      trackedParamValsRef.current = params;
      trackedParamNamesRef.current = trackedParams;
      logger.debug('fetch called by []')
      fetchContent().catch(() => {});
      setRefreshInterval(60000);
    }, []);

    useEffect(() => {
      let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
      logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
      if (shouldReFetch.changed) {
          trackedParamValsRef.current = shouldReFetch.paramRef;
          logger.debug('fetch called via changedParams', shouldReFetch.changed)
          fetchContent();
      }

      // make sure to clear the content if any of the tracked params were removed
      // common use case: when a parent param changes, it clears the child param
      for (const param of trackedParamNamesRef.current) { 
          if (!selectedViewParams[param]) {
            setTableRows([]);
            setTableColumns([]);
            return;
          }
      }
    }, [changedParams]);

    useEffect(() => {
      logger.debug('fetch called via config')
      fetchContent();
    }, [config]);

    // Start the interval only once we have a refreshInterval > 0
    useInterval(fetchContent, refreshInterval || null);

    return (
        <>
        {config?.title ? 
            <Typography color="text.secondary" variant="h6" margin={2} sx={{display: 'flex', alignItems: 'center'}}>
                {config.title}
            </Typography>
        : <></>}
        <Box width='100%'  sx={{ overflowX: 'auto', marginTop: 0 }}>
            <DataTable 
              columns={tableColumns} 
              rows={tableRows} 
              selectable={selectable} 
              onDeselectAll={onDeselectAll} 
              onDeselectOne={(_, row) => {
                onDeselectOne(row.id);
              }}
              onSelectAll={onSelectAll} 
              onSelectOne={(_, row) => {
                onSelectOne(row.id);
              }} 
              selected={selected}
            />
        </Box>
        </>
    );
}
